import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../components/utils/ResourceError";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { itemListsApi } from "../../../api/item_lists";
import {
  ClipboardDocumentListIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  DocumentCheckIcon,
  ArrowLeftIcon,
  PlusIcon,
  QrCodeIcon,
  MagnifyingGlassIcon,
  CameraIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { Button } from "@mui/material";
import { axiosWithToast } from "../../../components/Utils";
import ModernProductSelector from "../../../components/utils/Selector/ModernProductSelector";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { TextField } from "@mui/material";
import { fetchProducts } from "../../../api/product";
import axios from "../../../api/axios";
import { ItemListActionBar } from "../../../components/utils/ItemListActionBar";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { PMField } from "../../../components/utils/PMField";
import { PMButton } from "../../../components/utils/PMButton";
import toast from "react-hot-toast";
import ReportAddItemsDialog from "../../../components/dashboard/service/reports/report-add-items-dialog";

export const ItemList = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [itemList, setItemList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addProductDialogOpen, setAddProductDialogOpen] = useState(false);
  const [qrScanMode, setQrScanMode] = useState(false);
  const [manualSearchMode, setManualSearchMode] = useState(false);
  const [manualProductMode, setManualProductMode] = useState(false);
  const [bulkAddDialogOpen, setBulkAddDialogOpen] = useState(false);
  const [qrCodeInput, setQrCodeInput] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [cameraMode, setCameraMode] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    fetchItemList();
  }, [id]);

  const fetchItemList = async () => {
    try {
      setLoading(true);
      const response = await itemListsApi.getItemList(id);
      if (response?.data?.success) {
        setItemList(response.data.data);
      } else {
        setError("Kunne ikke hente vareliste");
      }
    } catch (err) {
      console.error("Error fetching item list:", err);
      setError("Kunne ikke hente vareliste");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    await axiosWithToast(
      itemListsApi.approveItemList(id, ""),
      () => fetchItemList(),
      () => {},
      () => {},
      "Godkjenner vareliste..."
    );
  };

  const handleReject = async () => {
    const comment = prompt("Skriv inn avslagsgrunn (valgfritt):");
    if (comment === null) return; // User cancelled

    await axiosWithToast(
      itemListsApi.rejectItemList(id, comment || ""),
      () => fetchItemList(),
      () => {},
      () => {},
      "Avslår vareliste..."
    );
  };

  const handleDelete = async () => {
    if (!confirm("Er du sikker på at du vil slette denne varelisten?")) return;

    await axiosWithToast(
      itemListsApi.deleteItemList(id),
      () => {
        // Navigate back to project or item lists page
        if (itemList?.module === "project" && itemList?.module_param) {
          navigate(`/dashboard/projects/${itemList.module_param}?tab=9`);
        } else {
          navigate("/dashboard/projects");
        }
      },
      () => {},
      () => {},
      "Sletter vareliste..."
    );
  };

  const handleSubmitForApproval = async () => {
    await axiosWithToast(
      itemListsApi.submitItemListForApproval(id),
      () => fetchItemList(),
      () => {},
      () => {},
      "Sender vareliste til godkjenning..."
    );
  };

  const handleMarkInvoicedManually = async () => {
    const comment = window.prompt(
      t("project.item_list.approval_comment") + " (valgfritt)"
    );
    if (comment === null) return;

    await axiosWithToast(
      itemListsApi.markItemListInvoicedManually(id, comment || ""),
      () => fetchItemList(),
      () => {},
      () => {},
      t("project.item_list.mark_invoiced_manually") + "..."
    );
  };

  const handlePrint = () => {
    window.print();
  };

  const handleRemoveItem = async (itemId) => {
    if (!confirm("Er du sikker på at du vil fjerne dette produktet fra listen?")) return;

    await axiosWithToast(
      itemListsApi.removeItemFromList(id, itemId),
      () => fetchItemList(),
      () => {},
      () => {},
      "Fjerner produkt..."
    );
  };

  const handleAddProduct = () => {
    setAddProductDialogOpen(true);
    setQrScanMode(false);
    setManualSearchMode(false);
    setManualProductMode(false);
    setBulkAddDialogOpen(false);
    setSelectedProducts([]);
    setQrCodeInput("");
    setQuantity(1);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' } // Use back camera on mobile
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    } catch (error) {
      console.error("Error accessing camera:", error);
      toast.error("Kunne ikke åpne kamera. Sjekk at du har gitt tillatelse.");
      setCameraMode(false);
    }
  };

  const stopCamera = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      const tracks = stream.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    // Cleanup camera when component unmounts or dialog closes
    return () => {
      stopCamera();
    };
  }, []);

  const handleAddProducts = async () => {
    if (selectedProducts.length === 0) {
      toast.error("Velg minst ett produkt");
      return;
    }

    try {
      // Ensure we have full product objects with all required fields
      const productsToAdd = await Promise.all(
        selectedProducts.map(async (product) => {
          // If product already has full data, use it
          if (product.title || product.displayTitle) {
            return product;
          }
          
          // Otherwise, fetch full product data
          try {
            const response = await fetchProducts({
              ids: product.id.toString(),
              page: 1,
              per_page: 1,
              fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
            });
            
            if (response?.data && response.data.length > 0) {
              return response.data[0];
            }
            return product; // Fallback to original if fetch fails
          } catch (error) {
            console.error("Error fetching product details:", error);
            return product; // Fallback to original if fetch fails
          }
        })
      );

      // Add all products to the item list
      for (const product of productsToAdd) {
        await itemListsApi.addItemToList(id, {
          product_id: product.id,
          quantity: product.quantity || quantity || 1,
          purchase_price: product.purchase_price || 0,
          sales_price: product.sales_price || 0,
        });
      }
      
      // Close dialog and reset state
      setAddProductDialogOpen(false);
      setSelectedProducts([]);
      setQuantity(1);
      setQrScanMode(false);
      setManualSearchMode(false);
      setQrCodeInput("");
      setCameraMode(false);
      stopCamera();
      
      fetchItemList();
      toast.success(`${productsToAdd.length} ${productsToAdd.length === 1 ? "produkt" : "produkter"} lagt til`);
    } catch (error) {
      console.error("Error adding products:", error);
      toast.error("Kunne ikke legge til produkter");
    }
  };

  // Helper functions for price formatting
  const formatPriceInput = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const stringValue = String(value);
    const cleaned = stringValue.replace(/[^0-9,.]/g, "");
    return cleaned.replace(/\./g, ",");
  };

  const formatPriceWithTwoDecimals = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    const stringValue = String(value).trim();
    if (stringValue === "") return "";
    if (stringValue.includes(",")) {
      const parts = stringValue.split(",");
      const integerPart = parts[0] || "";
      let decimalPart = parts[1] || "";
      decimalPart = decimalPart.substring(0, 2);
      if (decimalPart.length === 1) {
        decimalPart = decimalPart + "0";
      } else if (decimalPart.length === 0) {
        decimalPart = "00";
      }
      return `${integerPart},${decimalPart}`;
    }
    if (/^\d+$/.test(stringValue)) {
      return `${stringValue},00`;
    }
    return stringValue;
  };

  const parseNumericValue = (value) => {
    if (!value || value === "") return 0;
    const stringValue = String(value);
    const normalized = stringValue.replace(/,/g, ".");
    return parseFloat(normalized) || 0;
  };

  const handleAddItems = async (itemsToAdd) => {
    try {
      for (const item of itemsToAdd) {
        await itemListsApi.addItemToList(id, {
          product_id: item.product_id,
          description: item.description || "",
          quantity: item.quantity || 1,
          sales_price: item.sales_price || 0,
          purchase_price: item.purchase_price || 0,
          discount: item.discount || 0,
          surcharge: item.surcharge || 0,
        });
      }
      
      setBulkAddDialogOpen(false);
      fetchItemList();
      toast.success(`${itemsToAdd.length} ${itemsToAdd.length === 1 ? "produkt" : "produkter"} lagt til`);
    } catch (error) {
      console.error("Error adding items:", error);
      toast.error("Kunne ikke legge til produkter");
    }
  };

  const handleAddManualProduct = async (values) => {
    try {
      const newItem = {
        id: null,
        product_id: null,
        product: null,
        description: values.description || "",
        quantity: parseFloat(values.quantity) || 1,
        sales_price: values.sales_price ? parseNumericValue(values.sales_price) : 0,
        purchase_price: values.purchase_price ? parseNumericValue(values.purchase_price) : 0,
        discount: values.discount ? parseFloat(values.discount) : 0,
        surcharge: values.surcharge ? parseFloat(values.surcharge) : 0,
      };

      await handleAddItems([newItem]);
      setAddProductDialogOpen(false);
      setManualProductMode(false);
    } catch (error) {
      console.error("Error adding manual product:", error);
    }
  };

  const handleQrCodeScan = async (scannedCode) => {
    if (!scannedCode || scannedCode.trim() === "") {
      toast.error("Skriv inn eller skann en QR-kode");
      return;
    }

    // Stop camera if active
    if (cameraMode) {
      stopCamera();
      setCameraMode(false);
    }

    // Search for product by item_number (assuming QR code contains item_number)
    try {
      const response = await fetchProducts({
        query: scannedCode.trim(),
        page: 1,
        per_page: 10,
        fields: "id,title,displayTitle,item_number,unit,sales_price,purchase_price",
      });
      
      if (response && response.data && response.data.length > 0) {
        const product = response.data[0];
        setSelectedProducts([product]);
        setQrCodeInput("");
      } else {
        toast.error("Produkt ikke funnet. Prøv å søke manuelt.");
      }
    } catch (error) {
      console.error("Error searching product:", error);
      toast.error("Kunne ikke søke etter produkt");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "draft":
        return <PencilIcon className="h-4 w-4 text-gray-400" />;
      case "pending_approval":
        return <DocumentCheckIcon className="h-4 w-4 text-yellow-500" />;
      case "approved":
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case "rejected":
        return <TrashIcon className="h-4 w-4 text-red-500" />;
      case "invoiced_manually":
        return <CheckCircleIcon className="h-4 w-4 text-indigo-600" />;
      default:
        return <ClipboardDocumentListIcon className="h-4 w-4 text-gray-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "draft":
        return "bg-gray-100 text-gray-800";
      case "pending_approval":
        return "bg-yellow-100 text-yellow-800";
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "invoiced_manually":
        return "bg-indigo-100 text-indigo-900";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "draft":
        return t("project.item_list.status.draft");
      case "pending_approval":
        return t("project.item_list.status.pending_approval");
      case "approved":
        return t("project.item_list.status.approved");
      case "rejected":
        return t("project.item_list.status.rejected");
      case "invoiced_manually":
        return t("project.item_list.status.invoiced_manually");
      default:
        return status;
    }
  };

  const getTypeLabel = (type) => {
    return type === "consumption"
      ? t("project.item_list.type.consumption")
      : t("project.item_list.type.return");
  };

  const headerActions = [
    {
      component: null,
      label: t("project.item_list.print"),
      description: "",
      path: null,
      action: handlePrint,
      icon: PrinterIcon,
      textColor: "text-gray-700",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
    ...(itemList?.can_mark_invoiced_manually
      ? [
          {
            component: null,
            label: t("project.item_list.mark_invoiced_manually"),
            description: "",
            path: null,
            action: handleMarkInvoicedManually,
            icon: CheckCircleIcon,
            textColor: "text-indigo-600",
            bgColor: "bg-white",
            hidden: false,
            menu: true,
          },
        ]
      : []),
    {
      component: null,
      label: "Tilbake",
      description: "",
      path: null,
      action: () => {
        if (itemList?.module === "project" && itemList?.module_param) {
          navigate(`/dashboard/projects/${itemList.module_param}?tab=9`);
        } else {
          navigate("/dashboard/projects");
        }
      },
      icon: ArrowLeftIcon,
      textColor: "text-black",
      bgColor: "bg-white",
      hidden: false,
      menu: true,
    },
    ...(itemList?.can_edit && itemList?.status === "draft"
      ? [
          {
            component: null,
            label: "Send til godkjenning",
            description: "",
            path: null,
            action: handleSubmitForApproval,
            icon: DocumentCheckIcon,
            textColor: "text-blue-600",
            bgColor: "bg-white",
            hidden: false,
            menu: true,
          },
        ]
      : []),
    ...(itemList?.can_approve && itemList?.status === "pending_approval"
      ? [
          {
            component: null,
            label: "Godkjenn",
            description: "",
            path: null,
            action: handleApprove,
            icon: CheckCircleIcon,
            textColor: "text-green-600",
            bgColor: "bg-white",
            hidden: false,
            menu: true,
          },
          {
            component: null,
            label: "Avslå",
            description: "",
            path: null,
            action: handleReject,
            icon: TrashIcon,
            textColor: "text-red-600",
            bgColor: "bg-white",
            hidden: false,
            menu: true,
          },
        ]
      : []),
    ...(itemList?.can_delete
      ? [
          {
            component: null,
            label: "Slett",
            description: "",
            path: null,
            action: handleDelete,
            icon: TrashIcon,
            textColor: "text-red-600",
            bgColor: "bg-white",
            hidden: false,
            menu: true,
          },
        ]
      : []),
  ];

  if (loading) {
    return (
      <>
        <DashboardHeader title="Vareliste" subtitle="Laster..." />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceLoading />
        </div>
      </>
    );
  }

  if (error || !itemList) {
    return (
      <>
        <DashboardHeader title="Vareliste" subtitle="" />
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <ResourceError errors={[error || "Kunne ikke hente vareliste"]} />
        </div>
      </>
    );
  }

  return (
    <>
      <style>{`
        @media print {
          .item-list-no-print { display: none !important; }
          .item-list-print-root { display: block !important; }
        }
      `}</style>
      <div className="item-list-no-print">
        <DashboardHeader
        title={itemList.title || "Vareliste"}
        subtitle={`Status: ${getStatusLabel(itemList.status)}`}
        actions={headerActions}
        meta={[
          {
            component: (
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                  itemList.status
                )}`}
              >
                {getStatusIcon(itemList.status)}
                <span className="ml-1">{getStatusLabel(itemList.status)}</span>
              </span>
            ),
            label: "Status",
            description: "",
            path: null,
            action: null,
            icon: null,
            hidden: false,
          },
        ]}
        />
      </div>
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 pb-24 item-list-print-root">
        <div className="mt-8 space-y-6">
          {/* Item List Info */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("project.item_list.type")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {getTypeLabel(itemList.list_type)}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("project.item_list.total_items")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {itemList.total_items || 0}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("project.item_list.total_quantity")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {itemList.total_quantity || 0}
                </dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">
                  {t("project.item_list.total_value")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {itemList.total_value
                    ? `${parseFloat(itemList.total_value).toFixed(2)} kr`
                    : "0 kr"}
                </dd>
              </div>
            </div>

            {itemList.created_by && (
              <div className="mt-6 pt-6 border-t border-gray-200">
                <dt className="text-sm font-medium text-gray-500">
                  {t("project.item_list.created_by")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {itemList.created_by.name} -{" "}
                  {new Date(itemList.created_at).toLocaleDateString()}
                </dd>
              </div>
            )}

            {itemList.approved_by && (
              <div className="mt-4">
                <dt className="text-sm font-medium text-gray-500">
                  {t("project.item_list.approved_by")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {itemList.approved_by.name} -{" "}
                  {itemList.approved_at
                    ? new Date(itemList.approved_at).toLocaleDateString()
                    : ""}
                </dd>
              </div>
            )}

            {itemList.approval_comment && (
              <div className="mt-4">
                <dt className="text-sm font-medium text-gray-500">
                  {t("project.item_list.approval_comment")}
                </dt>
                <dd className="mt-1 text-sm text-gray-900">
                  {itemList.approval_comment}
                </dd>
              </div>
            )}
          </div>

          {/* Items Table */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center item-list-no-print">
              <h3 className="text-lg font-medium text-gray-900">
                {t("project.item_list.products")}
              </h3>
              {itemList?.can_edit && itemList?.status === "draft" && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PlusIcon className="h-5 w-5" />}
                  onClick={() => setAddProductDialogOpen(true)}
                >
                  {t("project.item_list.add_product")}
                </Button>
              )}
            </div>
            <div className="print-only px-6 py-3 border-b border-gray-200 hidden print:block">
              <h2 className="text-xl font-semibold text-gray-900">{itemList.title}</h2>
              <p className="text-sm text-gray-600">
                {t("project.item_list.status")}: {getStatusLabel(itemList.status)}
              </p>
            </div>
            {itemList.item_list_items && itemList.item_list_items.length > 0 ? (
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                    <TableRow>
                      <TableCell>{t("project.item_list.product")}</TableCell>
                      <TableCell align="right">
                        {t("project.item_list.quantity")}
                      </TableCell>
                      <TableCell align="right">
                        {t("project.item_list.purchase_price") || "Innkjøpspris"}
                      </TableCell>
                      <TableCell align="right">
                        {t("project.item_list.sales_price") || "Salgspris"}
                      </TableCell>
                      <TableCell align="right">
                        {t("project.item_list.total")}
                      </TableCell>
                      {itemList?.can_edit && itemList?.status === "draft" && (
                        <TableCell align="right">
                          {t("project.item_list.actions")}
                        </TableCell>
                      )}
                    </TableRow>
                </TableHead>
                <TableBody>
                  {itemList.item_list_items.map((item) => (
                    <TableRow key={item.id} hover>
                      <TableCell>
                        {item.order_item?.title ||
                          item.order_item?.product?.displayTitle ||
                          `Vare ${item.id}`}
                      </TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">
                        {item.order_item?.purchase_price
                          ? `${parseFloat(item.order_item.purchase_price).toFixed(2)} kr`
                          : "-"}
                      </TableCell>
                      <TableCell align="right">
                        {item.order_item?.sales_price
                          ? `${parseFloat(item.order_item.sales_price).toFixed(2)} kr`
                          : "-"}
                      </TableCell>
                      <TableCell align="right">
                        {item.order_item?.purchase_price && item.quantity
                          ? `${(
                              parseFloat(item.order_item.purchase_price) *
                              parseFloat(item.quantity)
                            ).toFixed(2)} kr`
                          : "-"}
                      </TableCell>
                      {itemList?.can_edit && itemList?.status === "draft" && (
                        <TableCell align="right">
                          <button
                            onClick={() => handleRemoveItem(item.id)}
                            className="text-red-600 hover:text-red-900"
                            title={t("project.item_list.remove")}
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {/* Total row */}
                  <TableRow>
                    <TableCell colSpan={itemList?.can_edit && itemList?.status === "draft" ? 3 : 2} align="right" sx={{ fontWeight: 'bold', borderTop: '2px solid #e5e7eb' }}>
                      {t("project.item_list.total_purchase")}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', borderTop: '2px solid #e5e7eb' }}>
                      {itemList.item_list_items.reduce((sum, item) => {
                        const purchasePrice = parseFloat(item.order_item?.purchase_price || 0);
                        const quantity = parseFloat(item.quantity || 0);
                        return sum + (purchasePrice * quantity);
                      }, 0).toFixed(2)} kr
                    </TableCell>
                    {itemList?.can_edit && itemList?.status === "draft" && (
                      <TableCell></TableCell>
                    )}
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <ClipboardDocumentListIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  {t("project.item_list.no_items")}
                </h3>
                <p className="text-gray-500">
                  {t("project.item_list.no_items_description")}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Product Dialog */}
      <Transition.Root show={addProductDialogOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => {
            stopCamera();
            setAddProductDialogOpen(false);
            setQrScanMode(false);
            setManualSearchMode(false);
            setManualProductMode(false);
            setCameraMode(false);
            setSelectedProducts([]);
            setQrCodeInput("");
            setQuantity(1);
          }}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div>
                    <Dialog.Title
                      as="h3"
                      className="text-lg font-medium leading-6 text-gray-900 mb-4"
                    >
                      {t("project.item_list.add_product")}
                    </Dialog.Title>

                    {!qrScanMode && !manualSearchMode && !manualProductMode && (
                      <div className="space-y-3">
                        <button
                          type="button"
                          onClick={() => setQrScanMode(true)}
                          className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        >
                          <QrCodeIcon className="h-5 w-5 mr-3 text-gray-600" />
                          <span className="text-gray-700 font-medium">{t("project.item_list.scan_qr")}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setManualSearchMode(true)}
                          className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        >
                          <MagnifyingGlassIcon className="h-5 w-5 mr-3 text-gray-600" />
                          <span className="text-gray-700 font-medium">{t("project.item_list.manual_search")}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setManualProductMode(true)}
                          className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        >
                          <PlusIcon className="h-5 w-5 mr-3 text-gray-600" />
                          <span className="text-gray-700 font-medium">Legg til manuelt produkt</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setBulkAddDialogOpen(true);
                            setAddProductDialogOpen(false);
                          }}
                          className="w-full flex items-center justify-start px-4 py-3 text-left border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors"
                        >
                          <PlusIcon className="h-5 w-5 mr-3 text-gray-600" />
                          <span className="text-gray-700 font-medium">Legg til produkter i bulk</span>
                        </button>
                      </div>
                    )}

                    {/* QR Code Scan Mode */}
                    {qrScanMode && (
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              setQrScanMode(false);
                              setQrCodeInput("");
                              setCameraMode(false);
                              stopCamera();
                            }}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            ← Tilbake
                          </button>
                        </div>

                        {/* Camera Mode */}
                        {cameraMode && (
                          <div className="space-y-4">
                            <div className="relative w-full h-64 bg-black rounded-lg overflow-hidden">
                              <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                className="w-full h-full object-cover"
                                style={{ transform: 'scaleX(-1)' }} // Mirror the video
                              />
                              <div className="absolute inset-0 flex items-center justify-center">
                                <div className="border-2 border-white rounded-lg w-48 h-48"></div>
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                setCameraMode(false);
                                stopCamera();
                              }}
                              className="w-full px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg text-gray-700 font-medium transition-colors"
                            >
                              Stopp kamera
                            </button>
                          </div>
                        )}

                        {/* Manual Input Mode */}
                        {!cameraMode && (
                          <div className="space-y-3">
                            <div className="relative">
                              <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Skriv produktnummer eller skann QR-kode"
                                value={qrCodeInput}
                                onChange={(e) => setQrCodeInput(e.target.value)}
                                onKeyPress={(e) => {
                                  if (e.key === "Enter") {
                                    handleQrCodeScan(qrCodeInput);
                                  }
                                }}
                                sx={{
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: "0.5rem",
                                    backgroundColor: "#f9fafb",
                                    paddingRight: "3rem",
                                    "&:hover": {
                                      backgroundColor: "#f3f4f6",
                                    },
                                    "&.Mui-focused": {
                                      backgroundColor: "#ffffff",
                                    },
                                  },
                                }}
                                autoFocus
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  setCameraMode(true);
                                  startCamera();
                                }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                title="Åpne kamera for QR-skanning"
                              >
                                <CameraIcon className="h-5 w-5" />
                              </button>
                            </div>
                            {qrCodeInput && (
                              <button
                                type="button"
                                onClick={() => handleQrCodeScan(qrCodeInput)}
                                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                              >
                                Søk
                              </button>
                            )}
                          </div>
                        )}
                        {selectedProducts.length > 0 && (
                          <div className="mt-4">
                            <div className="text-sm font-medium text-gray-700 mb-2">
                              Valgt produkt:
                            </div>
                            {selectedProducts.map((product) => (
                              <div key={product.id} className="p-2 bg-blue-50 rounded">
                                {product.displayTitle || product.title}
                              </div>
                            ))}
                            <div className="mt-4 flex space-x-2">
                              <Button
                                variant="contained"
                                onClick={handleAddProducts}
                                className="flex-1"
                              >
                                Legg til
                              </Button>
                              <Button
                                variant="outlined"
                                onClick={() => {
                                  setSelectedProducts([]);
                                  setQrCodeInput("");
                                }}
                              >
                                Nullstill
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Manual Product Mode */}
                    {manualProductMode && (
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              setManualProductMode(false);
                            }}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            ← Tilbake
                          </button>
                        </div>
                        <Formik
                          initialValues={{
                            description: "",
                            quantity: 1,
                            sales_price: "",
                            purchase_price: "",
                            discount: "",
                            surcharge: "",
                          }}
                          validationSchema={Yup.object({
                            quantity: Yup.number().min(0.01, "Antall må være større enn 0").required("Antall er påkrevd"),
                          })}
                          onSubmit={async (values, { setSubmitting }) => {
                            try {
                              await handleAddManualProduct(values);
                            } catch (error) {
                              console.error("Error adding manual product:", error);
                            } finally {
                              setSubmitting(false);
                            }
                          }}
                        >
                          {(formik) => (
                            <Form>
                              <div className="space-y-4">
                                <PMField
                                  name="description"
                                  label="Beskrivelse"
                                  type="text"
                                  {...formik}
                                />
                                <PMField
                                  name="quantity"
                                  label="Antall"
                                  type="number"
                                  step="0.01"
                                  min="0.01"
                                  {...formik}
                                />
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Innkjøpspris
                                    </label>
                                    <TextField
                                      fullWidth
                                      variant="standard"
                                      value={
                                        formik.values.purchase_price
                                          ? formatPriceInput(formik.values.purchase_price)
                                          : ""
                                      }
                                      onChange={(e) => {
                                        formik.setFieldValue(
                                          "purchase_price",
                                          formatPriceInput(e.target.value)
                                        );
                                      }}
                                      onBlur={(e) => {
                                        formik.handleBlur(e);
                                        if (e.target.value && e.target.value.trim() !== "") {
                                          const formatted = formatPriceWithTwoDecimals(e.target.value);
                                          formik.setFieldValue("purchase_price", formatted);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        const allowedKeys = /[0-9,.]/;
                                        const isNavigationKey = 
                                          e.key === "Backspace" || 
                                          e.key === "Delete" || 
                                          e.key === "ArrowLeft" || 
                                          e.key === "ArrowRight" || 
                                          e.key === "Tab" ||
                                          e.key === "Enter";
                                        
                                        if (!allowedKeys.test(e.key) && !isNavigationKey) {
                                          e.preventDefault();
                                        }
                                        if (e.key === ".") {
                                          e.preventDefault();
                                          const syntheticValue = (e.target.value || "") + ",";
                                          formik.setFieldValue("purchase_price", syntheticValue);
                                        }
                                      }}
                                      error={
                                        formik.touched.purchase_price &&
                                        Boolean(formik.errors.purchase_price)
                                      }
                                      helperText={
                                        formik.touched.purchase_price &&
                                        formik.errors.purchase_price
                                      }
                                      placeholder="0,00"
                                    />
                                  </div>
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                      Salgspris
                                    </label>
                                    <TextField
                                      fullWidth
                                      variant="standard"
                                      value={
                                        formik.values.sales_price
                                          ? formatPriceInput(formik.values.sales_price)
                                          : ""
                                      }
                                      onChange={(e) => {
                                        formik.setFieldValue(
                                          "sales_price",
                                          formatPriceInput(e.target.value)
                                        );
                                      }}
                                      onBlur={(e) => {
                                        formik.handleBlur(e);
                                        if (e.target.value && e.target.value.trim() !== "") {
                                          const formatted = formatPriceWithTwoDecimals(e.target.value);
                                          formik.setFieldValue("sales_price", formatted);
                                        }
                                      }}
                                      onKeyDown={(e) => {
                                        const allowedKeys = /[0-9,.]/;
                                        const isNavigationKey = 
                                          e.key === "Backspace" || 
                                          e.key === "Delete" || 
                                          e.key === "ArrowLeft" || 
                                          e.key === "ArrowRight" || 
                                          e.key === "Tab" ||
                                          e.key === "Enter";
                                        
                                        if (!allowedKeys.test(e.key) && !isNavigationKey) {
                                          e.preventDefault();
                                        }
                                        if (e.key === ".") {
                                          e.preventDefault();
                                          const syntheticValue = (e.target.value || "") + ",";
                                          formik.setFieldValue("sales_price", syntheticValue);
                                        }
                                      }}
                                      error={
                                        formik.touched.sales_price &&
                                        Boolean(formik.errors.sales_price)
                                      }
                                      helperText={
                                        formik.touched.sales_price &&
                                        formik.errors.sales_price
                                      }
                                      placeholder="0,00"
                                    />
                                  </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  <PMField
                                    name="discount"
                                    label="Rabatt (%)"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    {...formik}
                                  />
                                  <PMField
                                    name="surcharge"
                                    label="Påslag (%)"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    {...formik}
                                  />
                                </div>
                                <div className="flex justify-between items-center">
                                  <PMButton
                                    type="button"
                                    variant="outlined"
                                    size="small"
                                    onClick={() => {
                                      formik.resetForm();
                                      setManualProductMode(false);
                                    }}
                                  >
                                    Avbryt
                                  </PMButton>
                                  <PMButton
                                    type="submit"
                                    variant="contained"
                                    disabled={formik.isSubmitting}
                                    size="large"
                                  >
                                    Legg til
                                  </PMButton>
                                </div>
                              </div>
                            </Form>
                          )}
                        </Formik>
                      </div>
                    )}

                    {/* Manual Search Mode */}
                    {manualSearchMode && (
                      <div className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <button
                            type="button"
                            onClick={() => {
                              setManualSearchMode(false);
                              setSelectedProducts([]);
                            }}
                            className="text-gray-600 hover:text-gray-800"
                          >
                            ← Tilbake
                          </button>
                        </div>
                        <ModernProductSelector
                          open={true}
                          selectedProducts={selectedProducts}
                          onChange={(products) => {
                            setSelectedProducts(products);
                          }}
                          onClose={() => {
                            setManualSearchMode(false);
                          }}
                          multiple={true}
                          filters={[]}
                          showHeader={false}
                        />
                        {selectedProducts.length > 0 && (
                          <div className="mt-4 flex space-x-2">
                            <Button
                              variant="contained"
                              onClick={() => {
                                handleAddProducts();
                                setManualSearchMode(false);
                              }}
                              className="flex-1"
                            >
                              Legg til ({selectedProducts.length})
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setSelectedProducts([]);
                              }}
                            >
                              Nullstill
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Bulk Add Items Dialog */}
      <ReportAddItemsDialog
        open={bulkAddDialogOpen}
        onClose={() => {
          setBulkAddDialogOpen(false);
        }}
        onSave={(itemsToAdd) => {
          handleAddItems(itemsToAdd);
          setBulkAddDialogOpen(false);
        }}
        report={null}
        onSubDialogStateChange={() => {}}
      />

      {/* Action Bar for approval/rejection/submit */}
      <div className="item-list-no-print">
        <ItemListActionBar
          itemList={itemList}
          onApprove={handleApprove}
          onReject={handleReject}
          onSubmitForApproval={handleSubmitForApproval}
          onDelete={handleDelete}
          onMarkInvoicedManually={handleMarkInvoicedManually}
          show={
            (itemList?.can_edit && itemList?.status === "draft") ||
            (itemList?.can_approve && itemList?.status === "pending_approval") ||
            itemList?.can_delete ||
            itemList?.can_mark_invoiced_manually
          }
        />
      </div>
    </>
  );
};

